import { useState } from 'react'
import { Box, VStack, HStack, Stack, Text, Button, Spinner, Input } from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom'
import { ResponsiveTable, type ResponsiveColumn } from '../shared'
import { useUnionMembers } from '../../hooks/useUnion'
import type { UnionMember } from '../../types/union'

const PAGE_SIZE = 25

interface UnionMemberTableProps {
  unionId: string
  /** Total membership from the union detail — the count shown in the header. */
  memberCount: number
}

/**
 * Paginated + searchable member roster (DT-SCALE-1). Self-contained: owns its page + search state
 * and reads through the bounded GET /unions/{id}/members endpoint, so the union detail page never
 * loads the whole (tens-of-thousands) membership at once. Search follows the app's tap-first
 * pattern (input + Search button), not a debounce.
 */
export function UnionMemberTable({ unionId, memberCount }: UnionMemberTableProps) {
  const navigate = useNavigate()
  const [page, setPage] = useState(1)
  const [searchInput, setSearchInput] = useState('')
  const [search, setSearch] = useState<string | undefined>(undefined)

  const { data, isLoading } = useUnionMembers(unionId, { page, pageSize: PAGE_SIZE, search })

  const submitSearch = () => {
    setSearch(searchInput.trim() || undefined)
    setPage(1)
  }

  const totalPages = data ? Math.ceil(data.totalCount / data.pageSize) : 0

  const columns: ResponsiveColumn<UnionMember>[] = [
    {
      header: 'Name',
      mobileRole: 'title',
      cell: (m) => <Text fontWeight="medium" fontSize="sm">{m.contactName}</Text>,
    },
    {
      header: 'Position',
      mobileRole: 'meta',
      cell: (m) => <Text fontSize="sm">{m.position || '---'}</Text>,
    },
    {
      header: 'Employer',
      mobileRole: 'meta',
      cell: (m) => <Text fontSize="sm">{m.worksiteName}</Text>,
    },
    {
      header: 'Member #',
      mobileRole: 'meta',
      cell: (m) => <Text fontSize="sm">{m.unionMemberNumber || '---'}</Text>,
    },
  ]

  return (
    <Box bg="surface" p={{ base: 4, md: 6 }} borderRadius="lg" shadow="md">
      <Stack direction={{ base: 'column', md: 'row' }} justify="space-between" align={{ base: 'stretch', md: 'center' }} mb={4} gap={2}>
        <Text fontSize="lg" fontWeight="semibold">
          Members ({memberCount})
        </Text>
        <HStack gap={2}>
          <Input
            size="sm"
            minH="44px"
            maxW={{ base: 'full', md: '260px' }}
            placeholder="Search name or member #..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') submitSearch() }}
          />
          <Button size="sm" minH="44px" onClick={submitSearch}>Search</Button>
        </HStack>
      </Stack>

      {isLoading ? (
        <VStack py={6}>
          <Spinner size="md" color="accent" />
        </VStack>
      ) : !data || data.items.length === 0 ? (
        <Text color="muted" fontSize="sm">
          {search ? 'No members match your search.' : 'No union members.'}
        </Text>
      ) : (
        <>
          <ResponsiveTable
            bare
            columns={columns}
            rows={data.items}
            keyFn={(m) => m.employmentId}
            onRowClick={(m) => navigate(`/dashboard/contacts/${m.contactId}`)}
          />

          {totalPages > 1 && (
            <HStack justify="space-between" px={2} mt={4} flexWrap="wrap" gap={2}>
              <Text fontSize="sm" color="muted">
                Showing {((data.page - 1) * data.pageSize) + 1}--{Math.min(data.page * data.pageSize, data.totalCount)} of {data.totalCount}
              </Text>
              <HStack gap={2}>
                <Button size="sm" variant="outline" minH="44px" disabled={data.page <= 1}
                  onClick={() => setPage((prev) => prev - 1)}
                >Previous</Button>
                <Text fontSize="sm" color="muted">Page {data.page} of {totalPages}</Text>
                <Button size="sm" variant="outline" minH="44px" disabled={data.page >= totalPages}
                  onClick={() => setPage((prev) => prev + 1)}
                >Next</Button>
              </HStack>
            </HStack>
          )}
        </>
      )}
    </Box>
  );
}
